import type { CSSProperties } from 'react'
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'
import { canvasToScreen } from '../interaction/canvasToScreen'
import type { SelectedStationSnapshot } from '../interaction/selectedStationSnapshot'
import styles from './DesktopStationActionsPopup.module.css'

// Width is mirrored in DesktopStationActionsPopup.module.css (.host width).
// The JS copy is load-bearing because applyTransform() centres the popup
// horizontally on the station marker, which needs the half-width in CSS px.
const POPUP_WIDTH = 144

/**
 * How far below the station marker (in CSS px) the top edge of the
 * popup sits. The selectedStation BubbleOverlay extends UP from the
 * station marker; this popup sits BELOW so the two never overlap.
 * 12 px puts the popup roughly where the next bubble row would land
 * if the marker were duplicated, leaving a small visual breathing gap.
 */
const POPUP_OFFSET_BELOW_MARKER = 12

/**
 * Parity with BubbleOverlay / RouteBubbleOverlay: plain 200 ms opacity
 * fade. Keep in sync with the `.host` transition in
 * `DesktopStationActionsPopup.module.css` — JS reads this constant
 * for the leaving-popup unmount timer, CSS reads its own literal for
 * the visual transition. Two values must match.
 */
const FADE_MS = 200

export interface DesktopStationActionsPopupHandle {
  /** Called each rAF tick with the current transform; updates popup screen position imperatively. */
  apply(zoom: number, panX: number, panY: number, dpr: number): void
}

interface Props {
  /**
   * The currently-selected station whose actions this popup represents.
   * Null means "no station selected" — the popup fades out and unmounts.
   */
  selectedStation: SelectedStationSnapshot | null
  /**
   * Resolved by the caller from the desktop viewport check (>= 768 px).
   * False here also fades / unmounts the popup. Kept as a single boolean
   * so the component stays oblivious to the policy.
   */
  enabled: boolean
  /** Tap on the From button — caller dispatches `RouteFromSelected` + clears the selection. */
  onPickFrom: (station: SelectedStationSnapshot) => void
  /** As `onPickFrom`, for the To button. */
  onPickTo: (station: SelectedStationSnapshot) => void
  /**
   * Getter for the current map transform. Called in [useLayoutEffect]
   * so the popup is positioned BEFORE first paint — otherwise the
   * element ships untranslated for one frame (until the host's next
   * rAF tick fires `apply()`), visibly flashing at the viewport
   * origin. Must be a stable reference (wrap in `useCallback`
   * or pass the ref object itself) — it is called during layout.
   */
  getTransform: () => { zoom: number; panX: number; panY: number; dpr: number }
}

/** Fade-lifecycle state for the (single) popup instance. */
interface InFlightPopup {
  snapshot: SelectedStationSnapshot
  state: 'entering' | 'visible' | 'leaving'
}

/**
 * Desktop-only popup that lets the user assign the currently-selected
 * station to either From or To in the RouteCard picker. Renders
 * below the selectedStation BubbleOverlay's marker; fades in / out
 * to match the bubble's lifecycle.
 *
 * Visibility is `selectedStation !== null && enabled`. The caller
 * is responsible for resolving `enabled` from a viewport check
 * (>= 768 px) — the popup itself is policy-free.
 *
 * Position updates imperatively via [apply] each rAF tick so pan /
 * zoom keeps the popup anchored without React reconciliation. Mount-
 * time position is seeded by useLayoutEffect so newly-mounted popups
 * don't flash at (0, 0).
 *
 * Mirrors the simpler single-instance lifecycle from BubbleOverlay
 * (entering → visible on rAF, leaving for FADE_MS before unmount).
 * No swap concept — at most one popup is on screen.
 */
export const DesktopStationActionsPopup = forwardRef<DesktopStationActionsPopupHandle, Props>(
  function DesktopStationActionsPopup(
    { selectedStation, enabled, onPickFrom, onPickTo, getTransform },
    ref,
  ) {
    const [popups, setPopups] = useState<InFlightPopup[]>([])
    const positionRefs = useRef<Map<string, HTMLDivElement | null>>(new Map())

    // Effective input: the snapshot we should display, after gating on
    // `enabled`. When the viewport is mobile, behave as if no station
    // were selected — the popup fades out cleanly.
    const targetSnapshot = enabled ? selectedStation : null

    // Reconcile: keep / leave / enter, mirroring BubbleOverlay's pattern.
    // The `react-hooks/set-state-in-effect` rule discourages setState in
    // effects because of cascading renders; this is the same exception
    // BubbleOverlay calls out — leaving popups outlive the prop change
    // by FADE_MS and need state that persists across prop changes.
    useEffect(() => {
      setPopups((prev) => {
        const next: InFlightPopup[] = []
        const targetId = targetSnapshot?.id ?? null
        let addedTarget = false
        for (const p of prev) {
          if (p.snapshot.id === targetId) {
            // Same station still selected — keep visible (or revive a
            // 'leaving' instance if the user re-selected the same id
            // mid-fade-out).
            next.push({ snapshot: p.snapshot, state: 'visible' })
            addedTarget = true
          } else {
            next.push({ snapshot: p.snapshot, state: 'leaving' })
          }
        }
        if (targetSnapshot !== null && !addedTarget) {
          next.push({ snapshot: targetSnapshot, state: 'entering' })
        }
        return next
      })
    }, [targetSnapshot])

    // 'entering' → 'visible' after one rAF so the initial opacity:0
    // commits to the DOM before the transition to opacity:1 kicks in.
    // jsdom does not implement rAF — fall back to setTimeout(0) so the
    // state hop still fires under vi.useFakeTimers().
    useEffect(() => {
      const hasEntering = popups.some((p) => p.state === 'entering')
      if (!hasEntering) return
      const promote = () => {
        setPopups((prev) =>
          prev.map((p) => (p.state === 'entering' ? { ...p, state: 'visible' } : p)),
        )
      }
      if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
        const rafId = window.requestAnimationFrame(promote)
        return () => {
          window.cancelAnimationFrame(rafId)
        }
      }
      const timerId = window.setTimeout(promote, 0)
      return () => {
        window.clearTimeout(timerId)
      }
    }, [popups])

    // Unmount 'leaving' popups after FADE_MS so the opacity transition
    // can visibly play out before React removes the DOM node.
    useEffect(() => {
      const hasLeaving = popups.some((p) => p.state === 'leaving')
      if (!hasLeaving) return
      const timer = window.setTimeout(() => {
        setPopups((prev) => prev.filter((p) => p.state !== 'leaving'))
      }, FADE_MS)
      return () => {
        window.clearTimeout(timer)
      }
    }, [popups])

    const applyTransform = useCallback(
      (zoom: number, panX: number, panY: number, dpr: number) => {
        for (const p of popups) {
          const el = positionRefs.current.get(p.snapshot.id)
          if (el === null || el === undefined) continue
          const { x, y } = canvasToScreen(p.snapshot.canvasX, p.snapshot.canvasY, panX, panY, zoom)
          const cssX = x / dpr
          const cssY = y / dpr
          // Centre horizontally on the marker; sit just below it. Using
          // POPUP_OFFSET_BELOW_MARKER (rather than half POPUP_HEIGHT)
          // keeps the popup clear of the station marker glyph itself.
          // translate3d instead of left/top: compositor-only, no layout
          // invalidation per frame (Task 1.10). Host anchored at 0/0, so
          // the translate equals the old left/top values 1:1.
          el.style.transform = `translate3d(${cssX - POPUP_WIDTH / 2}px, ${cssY + POPUP_OFFSET_BELOW_MARKER}px, 0)`
        }
      },
      [popups],
    )

    useImperativeHandle(ref, () => ({ apply: applyTransform }), [applyTransform])

    // Position newly-mounted popups BEFORE first paint so they don't
    // flash at (0, 0) for one frame. Runs on every popups change.
    useLayoutEffect(() => {
      const t = getTransform()
      applyTransform(t.zoom, t.panX, t.panY, t.dpr)
    }, [popups, applyTransform, getTransform])

    return (
      <>
        {popups.map((p) => {
          const isVisible = p.state === 'visible'
          const className = `${styles.host}${isVisible ? ` ${styles.visible}` : ''}`
          // `position: absolute` is also declared in the CSS module; the
          // duplicate inline style is intentional for parity with
          // BubbleOverlay / RouteBubbleOverlay. Both are equivalent — the
          // CSS rule is the source of truth, the inline copy keeps the
          // host shape obvious in devtools. Anchored at the container
          // origin; per-frame positioning is a translate3d in
          // applyTransform (Task 1.10).
          const style: CSSProperties = { position: 'absolute', left: 0, top: 0 }
          return (
            <div
              key={p.snapshot.id}
              data-desktop-station-actions-host=""
              data-state={p.state}
              ref={(el) => {
                positionRefs.current.set(p.snapshot.id, el)
              }}
              className={className}
              style={style}
            >
              <button
                type="button"
                className={styles.button}
                onClick={() => onPickFrom(p.snapshot)}
                data-action="from"
              >
                From
              </button>
              <button
                type="button"
                className={styles.button}
                onClick={() => onPickTo(p.snapshot)}
                data-action="to"
              >
                To
              </button>
            </div>
          )
        })}
      </>
    )
  },
)
